import { useEffect, useRef } from 'react';

const DOMAINS = [
  { name: 'launchvault', tld: '.io',  avail: true  },
  { name: 'startup',     tld: '.com', avail: false },
  { name: 'swiftsync',   tld: '.co',  avail: true  },
  { name: 'founderkit',  tld: '.co',  avail: true  },
  { name: 'unicorn',     tld: '.ai',  avail: false },
  { name: 'blitzly',     tld: '.io',  avail: true  },
  { name: 'rocketbase',  tld: '.io',  avail: true  },
  { name: 'nextstep',    tld: '.com', avail: false },
  { name: 'buildfast',   tld: '.dev', avail: true  },
  { name: 'growthhq',    tld: '.com', avail: false },
  { name: 'snapdraft',   tld: '.ai',  avail: true  },
  { name: 'codepath',    tld: '.dev', avail: true  },
  { name: 'venture',     tld: '.io',  avail: false },
  { name: 'shipfast',    tld: '.dev', avail: true  },
  { name: 'stackblaze',  tld: '.io',  avail: true  },
  { name: 'brandforge',  tld: '.co',  avail: true  },
  { name: 'launchpad',   tld: '.com', avail: false },
  { name: 'ideaflow',    tld: '.io',  avail: true  },
  { name: 'sparkloop',   tld: '.co',  avail: true  },
  { name: 'mintmark',    tld: '.io',  avail: true  },
];

interface Point { x: number; y: number; }
interface Strike {
  main: Point[];
  forks: Point[][];
  t: number;
  duration: number;
  tx: number;
  ty: number;
  spawned: boolean;
}

function makeLightning(
  x1: number, y1: number, x2: number, y2: number,
  roughness: number, depth: number,
): Point[] {
  if (depth === 0) return [{ x: x1, y: y1 }, { x: x2, y: y2 }];
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  const px = -dy / len;
  const py = dx / len;
  const disp = (Math.random() - 0.5) * roughness * len;
  const nx = mx + px * disp;
  const ny = my + py * disp;
  return [
    ...makeLightning(x1, y1, nx, ny, roughness * 0.58, depth - 1).slice(0, -1),
    ...makeLightning(nx, ny, x2, y2, roughness * 0.58, depth - 1),
  ];
}

function buildBolt(sx: number, sy: number, tx: number, ty: number) {
  const main = makeLightning(sx, sy, tx, ty, 0.40, 8);
  const forks: Point[][] = [];
  const forkCount = 1 + Math.floor(Math.random() * 2);
  for (let f = 0; f < forkCount; f++) {
    const idx = Math.floor(main.length * (0.2 + Math.random() * 0.5));
    const fp = main[idx];
    const fex = fp.x + (Math.random() - 0.45) * 180;
    const fey = fp.y + (0.25 + Math.random() * 0.55) * (ty - sy);
    forks.push(makeLightning(fp.x, fp.y, fex, fey, 0.36, 6));
  }
  return { main, forks };
}

export default function LightningCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const badgeContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (window.innerWidth < 768) return;

    const canvas = canvasRef.current!;
    const container = badgeContainerRef.current!;
    const ctx = canvas.getContext('2d')!;
    let domainIdx = Math.floor(Math.random() * DOMAINS.length);

    function resize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    function spawnBadge(x: number, y: number) {
      const d = DOMAINS[domainIdx % DOMAINS.length];
      domainIdx = (domainIdx + 1) % DOMAINS.length;

      const el = document.createElement('div');
      el.style.cssText = [
        'position:absolute',
        'pointer-events:none',
        'display:inline-flex',
        'align-items:center',
        'gap:6px',
        'padding:5px 13px',
        'border-radius:20px',
        'font-size:12px',
        'font-weight:600',
        "font-family:'JetBrains Mono',monospace",
        'transform:translate(-50%,-50%)',
        'white-space:nowrap',
        'opacity:0',
        `left:${x}px`,
        `top:${y}px`,
        d.avail
          ? 'color:#86efac;border:1px solid rgba(74,222,128,0.35);background:rgba(16,185,129,0.10)'
          : 'color:#94a3b8;border:1px solid rgba(148,163,184,0.18);background:rgba(255,255,255,0.04)',
      ].join(';');

      const dot = document.createElement('span');
      dot.style.cssText = [
        'width:6px',
        'height:6px',
        'border-radius:50%',
        'flex-shrink:0',
        d.avail
          ? 'background:#4ade80;box-shadow:0 0 5px #4ade80'
          : 'background:#475569',
      ].join(';');

      el.appendChild(dot);
      el.appendChild(document.createTextNode(d.name + d.tld));
      container.appendChild(el);

      requestAnimationFrame(() => {
        el.style.transition = 'opacity 0.08s ease,box-shadow 0.08s ease,color 0.08s';
        el.style.opacity = '1';
        const glowColor = d.avail ? 'rgba(74,222,128,0.7)' : 'rgba(129,140,248,0.7)';
        const glow2 = d.avail ? 'rgba(16,185,129,0.25)' : 'rgba(99,102,241,0.20)';
        el.style.boxShadow = `0 0 14px ${glowColor},0 0 40px ${glow2}`;
        el.style.color = d.avail ? '#bbf7d0' : '#e0e7ff';
        el.style.borderColor = d.avail ? 'rgba(74,222,128,0.7)' : 'rgba(129,140,248,0.7)';

        setTimeout(() => {
          el.style.transition = 'box-shadow 0.9s ease,color 0.9s ease,border-color 0.9s ease';
          el.style.boxShadow = d.avail ? '0 0 4px rgba(74,222,128,0.2)' : 'none';
          el.style.color = d.avail ? '#86efac' : '#94a3b8';
          el.style.borderColor = d.avail ? 'rgba(74,222,128,0.25)' : 'rgba(148,163,184,0.15)';

          setTimeout(() => {
            el.style.transition = 'opacity 1.4s ease';
            el.style.opacity = '0';
            setTimeout(() => el.remove(), 1500);
          }, 1400);
        }, 350);
      });
    }

    function drawLine(
      pts: Point[], alpha: number, width: number,
      color: string, glow: string, blur: number,
    ) {
      if (pts.length < 2 || alpha <= 0) return;
      ctx.save();
      ctx.globalAlpha = Math.min(alpha, 1);
      ctx.shadowBlur = blur;
      ctx.shadowColor = glow;
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(pts[0].x, pts[0].y);
      for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i].x, pts[i].y);
      ctx.stroke();
      ctx.restore();
    }

    function pickTarget() {
      const W = canvas.width;
      const H = canvas.height;
      const contentHalf = 340;
      const cx = W / 2;
      const margin = 60;

      let tx: number;
      if (W > 900 && Math.random() < 0.72) {
        const leftEdge = cx - contentHalf - margin;
        const rightEdge = cx + contentHalf + margin;
        if (Math.random() < 0.5 && leftEdge > margin) {
          tx = margin + Math.random() * (leftEdge - margin);
        } else {
          tx = rightEdge + Math.random() * (W - rightEdge - margin);
        }
      } else {
        tx = margin + Math.random() * (W - margin * 2);
      }
      const ty = H * (0.15 + Math.random() * 0.68);
      return { tx, ty };
    }

    const strikes: Strike[] = [];
    let nextStrikeIn = 900 + Math.random() * 1200;
    let lastFrame: number | null = null;
    let rafId: number;

    function launchStrike() {
      const { tx, ty } = pickTarget();
      const ox = tx + (Math.random() - 0.5) * 280;
      const oy = -25 - Math.random() * 90;
      const bolt = buildBolt(ox, oy, tx, ty);
      strikes.push({ main: bolt.main, forks: bolt.forks, t: 0, duration: 460, tx, ty, spawned: false });
    }

    function animateFrame(now: number) {
      rafId = requestAnimationFrame(animateFrame);
      const dt = lastFrame !== null ? Math.min(now - lastFrame, 60) : 16;
      lastFrame = now;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      nextStrikeIn -= dt;
      if (nextStrikeIn <= 0) {
        launchStrike();
        nextStrikeIn = 2600 + Math.random() * 3200;
      }

      for (let i = strikes.length - 1; i >= 0; i--) {
        const s = strikes[i];
        s.t += dt / s.duration;
        if (s.t >= 1) { strikes.splice(i, 1); continue; }

        let alpha: number;
        if (s.t < 0.13) {
          alpha = s.t / 0.13;
        } else {
          alpha = 1 - (s.t - 0.13) / 0.87;
        }

        if (!s.spawned && s.t >= 0.13) {
          s.spawned = true;
          spawnBadge(s.tx, s.ty);
        }

        const gA = alpha * 0.52;
        const cA = alpha;

        drawLine(s.main, gA, 2.8, '#818cf8', '#6366f1', 30);
        drawLine(s.main, cA, 1.0, '#e0e7ff', '#c7d2fe', 7);

        for (const fork of s.forks) {
          drawLine(fork, gA * 0.65, 1.6, '#818cf8', '#6366f1', 18);
          drawLine(fork, cA * 0.60, 0.7, '#c7d2fe', '#a5b4fc', 4);
        }

        if (s.t < 0.30) {
          const tip = s.main[s.main.length - 1];
          const r = (1 - s.t / 0.30) * 28;
          const fa = (1 - s.t / 0.30) * alpha * 0.55;
          ctx.save();
          ctx.globalAlpha = fa;
          const g = ctx.createRadialGradient(tip.x, tip.y, 0, tip.x, tip.y, r);
          g.addColorStop(0, 'rgba(224,231,255,0.95)');
          g.addColorStop(0.35, 'rgba(99,102,241,0.5)');
          g.addColorStop(1, 'rgba(99,102,241,0)');
          ctx.fillStyle = g;
          ctx.beginPath();
          ctx.arc(tip.x, tip.y, r, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      }
    }

    rafId = requestAnimationFrame(animateFrame);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <>
      <canvas
        ref={canvasRef}
        style={{ position: 'fixed', inset: 0, zIndex: 1, pointerEvents: 'none' }}
      />
      <div
        ref={badgeContainerRef}
        style={{ position: 'fixed', inset: 0, zIndex: 2, pointerEvents: 'none' }}
      />
    </>
  );
}
